import { useQuery } from 'react-query'
import axios from 'src/utils/axios'
import { API_BASE_URL } from 'src/constants'
import { fetchWithPartner } from 'src/api/partner'

// The somatic structural tables of one analysis (roadmap R1.24b, R1.36, R1.37):
// gene CN, segments, SV and fusion events, pins, reviewer
// tiers and the fusion panel limit. Every answer carries a
// state (evaluated, or not_assessed with a reason): an empty table is never
// "none found" unless the state says evaluated.

const base = (awId) => `${API_BASE_URL}/structural/${awId}`

const get = async (url, params) => {
  const { data } = await axios.get(url, { params })
  return data
}

const options = (awId, enabled) => ({
  enabled: enabled && !!awId,
  retry: false,
  keepPreviousData: true,
  staleTime: 30_000,
})

export const structuralKey = (awId, kind, params = {}) => ['structural', awId, kind, JSON.stringify(params)]

export const useStructuralRuns = ({ awId, enabled = true }) =>
  useQuery(structuralKey(awId, 'runs'), () => get(`${base(awId)}/runs`), options(awId, enabled))

export const useStructuralGeneCn = ({ awId, params = {}, enabled = true }) =>
  useQuery(structuralKey(awId, 'gene-cn', params), () => get(`${base(awId)}/gene-cn`, params), options(awId, enabled))

export const useStructuralSegments = ({ awId, params = {}, enabled = true }) =>
  useQuery(structuralKey(awId, 'segments', params), () => get(`${base(awId)}/segments`, params), options(awId, enabled))

// `partner` (the RNA or DNA analysis on the other side) picks the pair the
// fusion agreement is read against; none means the primary pair.
export const useStructuralEvents = ({ awId, params = {}, partner = null, enabled = true }) =>
  useQuery(
    structuralKey(awId, 'events', partner ? { ...params, partner } : params),
    () => fetchWithPartner((p) => get(`${base(awId)}/events`, p), params, partner),
    options(awId, enabled),
  )

// One segment in full (libra batch I2): a ``view=list`` row leaves out its cancer
// genes, the tier suggestion's trail and the source's own fields; this serves them
// for the expanded row and the tier dialog.
export const useStructuralSegment = ({ awId, segmentId, enabled = true }) =>
  useQuery(
    structuralKey(awId, 'segment', { segmentId }),
    () => get(`${base(awId)}/segments/${segmentId}`),
    options(awId, enabled && segmentId != null),
  )

export const useFusionCoverage = ({ awId, enabled = true }) =>
  useQuery(structuralKey(awId, 'fusion-coverage'), () => get(`${base(awId)}/fusion-coverage`), options(awId, enabled))

// The IGV .seg export, fetched with the session's credentials (a plain link
// would not carry the token) and handed to the browser as a file.
export const downloadSeg = async (awId, { run = null, role = 'all' } = {}) => {
  const params = { sample_role: role }
  if (run) params.run = run
  const response = await axios.get(`${base(awId)}/segments.seg`, { params, responseType: 'blob' })
  const disposition = response.headers?.['content-disposition'] || ''
  const match = /filename="([^"]+)"/.exec(disposition)
  const url = window.URL.createObjectURL(response.data)
  const a = document.createElement('a')
  a.href = url
  a.download = match ? match[1] : `structural_${awId}.seg`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

// Reviewer tier on one row (target_type gene_cn, segment or event). An
// evidence letter decides the tier (A/B -> I, C/D -> II); tier and letter both
// null clears it, keeping the history.
export const setStructuralTier = async (awId, { targetType, targetId, tier, evidenceLevel, rationale }) => {
  const { data } = await axios.post(`${base(awId)}/tiers`, {
    target_type: targetType,
    target_id: targetId,
    tier: tier || null,
    evidence_level: evidenceLevel || null,
    rationale: rationale || '',
  })
  return data
}

export const toggleStructuralBookmark = async (awId, { targetType, targetId }) => {
  const { data } = await axios.post(`${base(awId)}/bookmarks`, { target_type: targetType, target_id: targetId })
  return data
}

// Copy number by exon of one gene (MLPA-style view). The server answers 404
// for a gene it does not know or an analysis outside the caller's organisation,
// and 400 for a germline analysis; both mean "nothing to show", never an error
// banner, so the hook resolves them to null.
export const fetchGeneExons = async (awId, gene, transcript) => {
  try {
    return await get(`${base(awId)}/gene-exons`, transcript ? { gene, transcript } : { gene })
  } catch (error) {
    const status = error?.response?.status ?? error?.status
    if (status === 404 || status === 400) return null
    throw error
  }
}

export const useGeneExons = ({ awId, gene, transcript = null, enabled = true }) =>
  useQuery(
    structuralKey(awId, 'gene-exons', { gene, transcript }),
    () => fetchGeneExons(awId, gene, transcript),
    options(awId, enabled && !!gene),
  )

// Paired DNA + RNA of one patient: the linked analyses, every RNA and DNA event
// with its agreement state, the counts, the header flag and the expressed-allele
// summaries, for one pair (`partner`, else the primary one); `pairs` lists
// every pair the analysis is in. 404/400 (no analysis, germline) resolve to null.
//
// `view` trims the answer (the full one is several MB):
// * 'counts': the counts per class, `pair_counts` and the link state of every
//   pair only (no event lists, no header flag, no expressed-allele summaries);
// * 'summary': everything but the row lists (event lists, copy-number rows,
//   expressed-allele sites).
// 'counts' is asked as `summary=1`, which a server without it ignores (serving
// the full answer); 'summary' is a view every current server serves.
export const RECONCILIATION_VIEW_PARAMS = {
  full: undefined,
  summary: { view: 'summary' },
  counts: { summary: 1 },
}

export const fetchReconciliation = async (awId, partner = null, view = 'full') => {
  try {
    return await fetchWithPartner(
      (p) => get(`${base(awId)}/reconciliation`, p),
      RECONCILIATION_VIEW_PARAMS[view] || undefined,
      partner,
    )
  } catch (error) {
    const status = error?.response?.status ?? error?.status
    if (status === 404 || status === 400) return null
    throw error
  }
}

export const useReconciliation = ({ awId, partner = null, enabled = true, view = 'full' }) => {
  const keyParams = { ...(partner ? { partner } : {}), ...(view && view !== 'full' ? { view } : {}) }
  return useQuery(
    structuralKey(awId, 'reconciliation', keyParams),
    () => fetchReconciliation(awId, partner, view),
    options(awId, enabled),
  )
}

// The fusion and splice-variant counts of the pair (`pair_counts`, one answer for
// both pages of a pair): { state, reason_text, fusion: { seen_in_both, rna_only,
// dna_only }, splice_variant: {...} }, each count with its state, its number
// (null when not assessed) and its reason_text. Null when the server does not
// send it (the caller reads `counts[side]` then).
export const pairCountsOf = (answer) => {
  const pc = answer?.pair_counts
  return pc && typeof pc === 'object' && pc.fusion ? pc : null
}
